/**
 * Recall Lifecycle Records Service
 *
 * One row per (vin, campaign_number, tenant_id), tracking where a recall has
 * reached in the workflow:
 *
 *     selected -> assigned -> re_scheduled | re_assigned -> in_progress
 *                                                       -> completed | cancelled
 *
 * The state machine itself — ranks, ownership, and what may be written over
 * what — lives in ./lifecycleState.ts, which is pure and directly tested.
 *
 * Two things this file used to claim that were not true, corrected here because
 * both misled readers into unsafe changes:
 *
 *   1. "Records are upserted at each step to maintain a complete history." The
 *      unique index `idx_recall_lifecycle_vin_campaign_tenant` permits exactly
 *      ONE row per recall, forever, so this table cannot hold per-attempt
 *      history and never could. It holds current state plus event timestamps.
 *
 *   2. The write was not an upsert. It read the row, spread it into the new
 *      body, then branched between UPDATE and INSERT — check-then-act, which
 *      loses concurrent updates and silently fell through to INSERT whenever
 *      the read errored, because that error was discarded.
 *
 * `lifecycle_state` is load-bearing, not analytical: the paginated recall RPC
 * hides finished work with `lifecycle_state IN ('completed','cancelled')`.
 * Corrupting this column puts completed work back in front of dispatchers, and
 * that is exactly how a technician came to be dispatched twice to a car he had
 * already finished. Every write below is guarded accordingly.
 */

import { getProdSupabaseClient } from './client';
import { getTenantIdFromSession } from './tenantScoping';
import { logSystemError } from '@/lib/utils/errorTranslator';
import {
  type LifecycleState,
  getLowerRankedStates,
  isWebWritableState,
  isTerminalState,
  buildLifecyclePayload,
  pickRefusedAdvanceFields,
  planReconciliation,
  chooseReconciliationArm,
  finalStatusFilter,
  partitionUpsertRows,
  type ReconciliationPlan,
  stripUndefined
} from './lifecycleState';
import { isFinalAssignmentStatus } from './assignmentStatus';

export type { LifecycleState };

export interface RecallLifecycleRecord {
  id?: string;
  tenant_id: string;
  vin: string;
  campaign_number: string;
  assignment_id?: string | null;

  // Selection details
  selected_by_user_id?: string;
  selected_at?: string;
  selection_date_added?: string;
  selection_date_modified?: string;

  // Technician assignment details.
  //
  // `| null` on a field is not decoration: it marks a column the recycle reset
  // deliberately CLEARS. Omitting a key preserves the column, sending null
  // erases it, and both behaviours are needed — so the type distinguishes the
  // fields we may legitimately erase from those we may not.
  //
  // Note which fields below are conspicuously NOT nullable: completed_at,
  // serviced_by_technician_id and serviced_by_technician_name. That is P3
  // expressed in the type system — the completion record is immutable, so
  // writing null to it should not typecheck.
  technician_id?: string | null;
  assigned_by_user_id?: string;
  assigned_at?: string;
  expected_visit_date?: string | null;
  started_at?: string | null;
  priority?: string;
  estimated_duration_minutes?: number;

  // Status details
  final_status?: string | null;
  assignment_status?: string | null;
  completion_notes?: string | null;
  other_reason?: string | null;
  serviced_by_technician_id?: string;
  serviced_by_technician_name?: string;
  is_preferred?: boolean;

  // Lifecycle state tracking
  lifecycle_state?: LifecycleState;
  lifecycle_state_updated_at?: string;

  // Event timestamps
  confirmed_at?: string;
  viewed_at?: string;
  printed_at?: string;
  printed_by_user_id?: string;
  re_assigned_at?: string;
  re_assigned_from_technician_id?: string;
  re_assigned_from_technician_name?: string;
  completed_at?: string;
  status_updated_at?: string;

  // Metadata
  created_at?: string;
  updated_at?: string;
}

/** Identity of a lifecycle row — also the unique index and the conflict target. */
interface RecallIdentity {
  vin: string;
  campaignNumber: string;
  tenantId: string;
}

/**
 * The assignment fields the lifecycle writers read.
 *
 * Declared explicitly because the shared Supabase client is untyped, so every
 * `.select()` resolves to `never` and each property access below would
 * otherwise be an error. Asserting once, here, is the same workaround this file
 * already used inline — but in one place instead of four, and it collapses
 * three near-identical queries into one helper.
 */
interface AssignmentSnapshot {
  vin: string;
  campaign_number: string;
  technician_id?: string | null;
  assigned_at?: string | null;
  expected_visit_date?: string | null;
  priority?: string | null;
  estimated_duration_minutes?: number | null;
  assignment_status?: string | null;
  completed_at?: string | null;
  notes?: string | null;
}

const ASSIGNMENT_COLUMNS =
  'vin, campaign_number, technician_id, assigned_at, expected_visit_date, priority, ' +
  'estimated_duration_minutes, assignment_status, completed_at, notes';

/**
 * Read the assignment a lifecycle write is about. Returns null when it is gone,
 * which every caller treats as "nothing to record".
 */
async function fetchAssignmentSnapshot(
  assignmentId: string,
  tenantId: string
): Promise<AssignmentSnapshot | null> {
  const prodClient = getProdSupabaseClient();
  const { data, error } = await prodClient
    .from('technician_assignments')
    .select(ASSIGNMENT_COLUMNS)
    .eq('id', assignmentId)
    .eq('tenant_id', tenantId)
    .maybeSingle();

  if (error) {
    logSystemError(error, 'reading an assignment for lifecycle tracking');
    return null;
  }
  return (data as AssignmentSnapshot | null) ?? null;
}

interface LifecycleWriteOptions {
  /**
   * States this particular write may move BACKWARDS from, beyond the ordinary
   * "advance only" rule.
   *
   * An explicit list rather than a boolean `force`, because a blanket bypass is
   * how the duplicate-dispatch incident happened: selection was granted
   * unconditional regression so a cancelled recall could be revived, and that
   * same grant also permitted `completed -> selected`.
   *
   * Naming the permitted origins keeps every write a guarded conditional UPDATE
   * — the list simply widens the `.in()` predicate — so there is no unguarded
   * write path anywhere in this file. A terminal state may never appear here;
   * assertNoTerminalRegression enforces that.
   */
  allowRegressionFrom?: readonly LifecycleState[];
}

/**
 * P3, enforced rather than documented: no write may claim the right to move a
 * recall out of `completed` or `cancelled`.
 *
 * A programming error, not a user error — so it throws rather than refusing
 * quietly, and the unit tests cover the intended callers.
 */
function assertNoTerminalRegression(from: readonly LifecycleState[]): void {
  const terminal = from.filter(isTerminalState);
  if (terminal.length > 0) {
    throw new Error(
      '[recallLifecycle] Refusing to permit regression from terminal state(s): ' +
        terminal.join(', ')
    );
  }
}

/**
 * Backfill selection provenance for a row we are about to create.
 *
 * Only called on the create path. It used to run on EVERY lifecycle write,
 * which is a second query per recall on a page that touches ~200 of them.
 * A row that already exists already has this data.
 */
async function fetchSelectionDetails(
  identity: RecallIdentity
): Promise<Partial<RecallLifecycleRecord>> {
  const prodClient = getProdSupabaseClient();
  const { data, error } = await prodClient
    .from('selected_recalls')
    .select('user_id, date_added, date_modified')
    .eq('vin', identity.vin)
    .eq('campaign_number', identity.campaignNumber)
    .eq('tenant_id', identity.tenantId)
    .order('date_added', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    // Provenance is a nice-to-have; never block the state write for it.
    logSystemError(error, 'fetching selection details for a new lifecycle record');
    return {};
  }

  const selected = data as {
    user_id?: string;
    date_added?: string;
    date_modified?: string;
  } | null;
  if (!selected) return {};

  return stripUndefined({
    selected_by_user_id: selected.user_id,
    selected_at: selected.date_added,
    selection_date_added: selected.date_added,
    selection_date_modified: selected.date_modified
  });
}

/**
 * Write a lifecycle state transition. The guard is evaluated by Postgres inside
 * the UPDATE, so there is no window between deciding and writing.
 *
 * Three steps, and the ordering is the whole design:
 *
 *   1. CONDITIONAL ADVANCE. One UPDATE matching only rows whose current state
 *      is strictly lower-ranked than the target (plus any explicitly permitted
 *      regression origins). Matched a row? Done — one round trip, no race.
 *
 *   2. CREATE. Zero rows matched could mean the row does not exist yet. Upsert
 *      with `ignoreDuplicates` so a concurrent creator wins harmlessly instead
 *      of us overwriting them.
 *
 *   3. REFUSED. Still nothing: the row exists at an equal-or-higher rank. The
 *      state write is dropped, but event timestamps are still recorded — see
 *      pickRefusedAdvanceFields. Only `lifecycle_state` is monotonic.
 *
 * The previous implementation read the state, decided in JavaScript, then wrote
 * in a second round trip. At the observed peak of 3,352 writes/minute against
 * this endpoint, a view whose read saw `assigned` could land after a completion
 * and clobber it. The guard was correct and still unsound; correctness of the
 * comparison was never the problem, its position was.
 */
async function writeLifecycleRecord(
  identity: RecallIdentity,
  targetState: LifecycleState,
  updates: Partial<RecallLifecycleRecord>,
  options: LifecycleWriteOptions = {}
): Promise<void> {
  if (!isWebWritableState(targetState)) {
    // The write contract, enforced at runtime as well as in the type system.
    // `in_progress` is app-only: it means a technician started the work, which
    // only phone data can honestly claim.
    throw new Error(`[recallLifecycle] The web may not write lifecycle state '${targetState}'`);
  }

  const allowRegressionFrom = options.allowRegressionFrom ?? [];
  assertNoTerminalRegression(allowRegressionFrom);

  const now = new Date().toISOString();
  const payload = buildLifecyclePayload(
    { vin: identity.vin, campaign_number: identity.campaignNumber, tenant_id: identity.tenantId },
    {
      ...updates,
      lifecycle_state: targetState,
      lifecycle_state_updated_at: now,
      updated_at: now
    } as Record<string, unknown>
  );

  // Anything at a strictly lower rank, plus the origins this write explicitly
  // permits. `.in()` matches neither NULL nor an unrecognised value, so the
  // policy is "advance only from a known, lower state" — fail closed.
  const advanceFrom = [...getLowerRankedStates(targetState), ...allowRegressionFrom];

  if (await tryAdvanceState(identity, payload, advanceFrom)) return;
  if (await tryCreateRecord(identity, payload)) return;
  await recordEventFieldsOnly(identity, payload);
}

/** Step 1 — the guarded advance. Resolves true when it matched a row. */
async function tryAdvanceState(
  identity: RecallIdentity,
  payload: Record<string, unknown>,
  advanceFrom: readonly LifecycleState[]
): Promise<boolean> {
  const prodClient = getProdSupabaseClient();
  const { data, error } = await prodClient
    .from('recall_lifecycle_records')
    .update(payload as never)
    .eq('vin', identity.vin)
    .eq('campaign_number', identity.campaignNumber)
    .eq('tenant_id', identity.tenantId)
    .in('lifecycle_state', advanceFrom)
    .select('id');

  if (error) throw error;
  return (data?.length ?? 0) > 0;
}

/**
 * Step 2 — no row matched, so it may not exist yet.
 *
 * `ignoreDuplicates` matters: if a concurrent writer created the row between
 * step 1 and here, they win and we fall through to step 3 rather than
 * overwriting whatever they just established.
 */
async function tryCreateRecord(
  identity: RecallIdentity,
  payload: Record<string, unknown>
): Promise<boolean> {
  const prodClient = getProdSupabaseClient();
  const selectionDetails = await fetchSelectionDetails(identity);
  const { data, error } = await prodClient
    .from('recall_lifecycle_records')
    .upsert({ ...selectionDetails, ...payload } as never, {
      onConflict: 'vin,campaign_number,tenant_id',
      ignoreDuplicates: true
    })
    .select('id');

  if (error) throw error;
  return (data?.length ?? 0) > 0;
}

/**
 * Step 3 — the advance was REFUSED: the row sits at an equal or higher rank,
 * and may be `completed`.
 *
 * The state write is dropped entirely. Only the event timestamps are recorded,
 * because someone genuinely did view or print this recall and saying so cannot
 * harm a completion record. Everything else in the payload — technician,
 * final_status, assignment_status — is discarded by pickRefusedAdvanceFields.
 */
async function recordEventFieldsOnly(
  identity: RecallIdentity,
  payload: Record<string, unknown>
): Promise<void> {
  const safeFields = pickRefusedAdvanceFields(payload);
  if (Object.keys(safeFields).length === 0) return;

  const prodClient = getProdSupabaseClient();
  const { error } = await prodClient
    .from('recall_lifecycle_records')
    .update(safeFields as never)
    .eq('vin', identity.vin)
    .eq('campaign_number', identity.campaignNumber)
    .eq('tenant_id', identity.tenantId)
    .select('id');

  if (error) throw error;
}

/**
 * Record an event against a recall WITHOUT touching its lifecycle state.
 *
 * Viewing, printing and confirming are events, not workflow stages, and each
 * already has its own timestamp column. Writing them as states is what caused
 * the damage this whole change addresses: a route preview would set 25,279 rows
 * to `viewed`, discarding whatever stage they had actually reached — including
 * `completed`. The information was never lost by *recording* the event; it was
 * lost by recording it in a column that holds one value at a time.
 *
 * So these paths no longer compete for `lifecycle_state` at all. The clobbering
 * is not merely guarded against, it is unreachable — there is no state to
 * clobber with. That also makes these writes unconditional and cheap, which is
 * what lets the route-preview fan-out be batched.
 *
 * Nothing is lost (P2): `viewed_at`, `printed_at` and `confirmed_at` were
 * already being written alongside the state, and they can hold every event.
 */
async function recordLifecycleEvent(
  identity: RecallIdentity,
  events: Partial<RecallLifecycleRecord>,
  options: { createIfMissing?: boolean } = {}
): Promise<void> {
  const prodClient = getProdSupabaseClient();
  const now = new Date().toISOString();

  const payload = buildLifecyclePayload(
    { vin: identity.vin, campaign_number: identity.campaignNumber, tenant_id: identity.tenantId },
    { ...events, updated_at: now } as Record<string, unknown>
  );

  const { data: updated, error: updateError } = await prodClient
    .from('recall_lifecycle_records')
    .update(payload as never)
    .eq('vin', identity.vin)
    .eq('campaign_number', identity.campaignNumber)
    .eq('tenant_id', identity.tenantId)
    .select('id');

  if (updateError) throw updateError;
  if (updated && updated.length > 0) return;

  // A caller that knows the recall is already finished must not create a row
  // here: a row created by an event starts at `selected`, which would put
  // finished work straight back into the pool.
  if (options.createIfMissing === false) return;

  // No row yet. Create one at the lowest real stage rather than relying on the
  // column default: a recall reaching a route preview has at minimum been
  // selected, and any later write advances it from there. Stating it explicitly
  // means a partial insert never inherits a stage nobody chose.
  const selectionDetails = await fetchSelectionDetails(identity);
  const { error: createError } = await prodClient
    .from('recall_lifecycle_records')
    .upsert({ ...selectionDetails, ...payload, lifecycle_state: 'selected' } as never, {
      onConflict: 'vin,campaign_number,tenant_id',
      ignoreDuplicates: true
    })
    .select('id');

  if (createError) throw createError;
}

/**
 * Update lifecycle record when recall is selected (added to route)
 */
export async function updateLifecycleOnSelection(
  vin: string,
  campaignNumber: string,
  userId: string
): Promise<void> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) {
      return;
    }

    const now = new Date().toISOString();
    await writeLifecycleRecord(
      { vin, campaignNumber, tenantId },
      'selected',
      {
        selected_at: now,
        selected_by_user_id: userId
      },
      {
        // G2. Reviving a CANCELLED recall is a legitimate business decision —
        // the RPC hides it, so without this it could never come back.
        //
        // Reviving a COMPLETED one is the bug. This path previously carried a
        // blanket regression grant, which permitted both; on 2026-09-08 a recall
        // completed on 31 August was re-selected through exactly here and then
        // re-assigned to the same technician who had already done the work.
        // Naming the one permitted origin is the fix.
        allowRegressionFrom: ['cancelled']
      }
    );
  } catch (error) {
    logSystemError(error, 'recording recall selection');
    // Non-critical: selection itself already succeeded.
  }
}

/**
 * Update lifecycle record when recall is assigned to technician
 */
export async function updateLifecycleOnAssignment(
  assignmentId: string,
  assignmentData: {
    technicianId: string;
    assignedBy?: string;
    expectedVisitDate?: string;
    priority?: string;
    estimatedDuration?: number;
  }
): Promise<void> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) return;

    const assignment = await fetchAssignmentSnapshot(assignmentId, tenantId);
    if (!assignment) return;

    // Conditional spreads rather than `field: value ?? undefined`, because under
    // exactOptionalPropertyTypes an optional property does not accept an
    // explicit undefined. It also matches what we want on the wire: a key that
    // is absent leaves the column alone.
    const visitDate = assignmentData.expectedVisitDate ?? assignment.expected_visit_date;
    const priority = assignmentData.priority ?? assignment.priority;
    const duration = assignmentData.estimatedDuration ?? assignment.estimated_duration_minutes;

    const now = new Date().toISOString();
    await writeLifecycleRecord(
      { vin: assignment.vin, campaignNumber: assignment.campaign_number, tenantId },
      'assigned',
      {
        assignment_id: assignmentId,
        assigned_at: assignment.assigned_at ?? now,
        technician_id: assignmentData.technicianId,
        assignment_status: 'assigned',
        ...(assignmentData.assignedBy ? { assigned_by_user_id: assignmentData.assignedBy } : {}),
        ...(visitDate ? { expected_visit_date: visitDate } : {}),
        ...(priority ? { priority } : {}),
        ...(duration != null ? { estimated_duration_minutes: duration } : {})
      }
      // No regression permitted. A recycled recall sits in `selected` (rank 30)
      // and advances here freely; a completed one cannot, which is the point.
    );
  } catch (error) {
    logSystemError(error, 'recording a technician assignment');
    // Don't throw - this is non-critical
  }
}

/**
 * Update lifecycle record when recall is re-assigned to different technician
 */
export async function updateLifecycleOnReAssignment(
  assignmentId: string,
  previousTechnicianId: string,
  previousTechnicianName: string,
  newAssignmentData: {
    technicianId: string;
    expectedVisitDate?: string;
  }
): Promise<void> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) return;

    const assignment = await fetchAssignmentSnapshot(assignmentId, tenantId);
    if (!assignment) return;

    const reAssignedVisitDate =
      newAssignmentData.expectedVisitDate ?? assignment.expected_visit_date;
    const reAssignedNow = new Date().toISOString();

    await writeLifecycleRecord(
      { vin: assignment.vin, campaignNumber: assignment.campaign_number, tenantId },
      're_assigned',
      {
        re_assigned_at: reAssignedNow,
        re_assigned_from_technician_id: previousTechnicianId,
        re_assigned_from_technician_name: previousTechnicianName,
        technician_id: newAssignmentData.technicianId,
        assignment_status: 'assigned',
        ...(reAssignedVisitDate ? { expected_visit_date: reAssignedVisitDate } : {})
      },
      {
        // A handoff must be recordable even after the technician has started —
        // that is precisely when work gets passed on. `in_progress` (60) is
        // above `re_assigned` (50), so it needs naming here.
        //
        // Terminal states are absent and cannot be added: this bypass exists for
        // reassigning live work, never for reopening finished work.
        allowRegressionFrom: ['in_progress']
      }
    );
  } catch (error) {
    logSystemError(error, 'recording a re-assignment');
    // Don't throw - this is non-critical
  }
}

/**
 * What the caller already knows about the assignment behind a recall.
 *
 * Passed by surfaces that hold the assignment row anyway — the route preview
 * iterates them to render the route — so reconciliation costs no extra query.
 */
export interface AssignmentEvidence {
  assignmentId?: string | undefined;
  assignmentStatus?: string | null | undefined;
  completedAt?: string | null | undefined;
}

/**
 * Repair a lifecycle record whose completion was lost.
 *
 * ── The defect this exists for (RLM-63) ──
 *
 * 140 recalls are marked `completed` on their assignment while their lifecycle
 * record still reads `final_status = NULL`, `completed_at = NULL`. The
 * completion never reached the lifecycle table at all.
 *
 * The cause is the mobile app, and it is over-represented 10x: 52% of the lost
 * completions carry a `service_events` row (the app's fingerprint) against 5.2%
 * of the recorded ones. `vehix-app/services/lifecycle_service.ts` writes
 *
 *     .update(update).eq('assignment_id', assignmentId)
 *
 * but the table is keyed on (vin, campaign_number, tenant_id), and
 * `assignment_id` is NULL on 79.9% of lifecycle rows — rows created by a view
 * or print event never had one, and `ON DELETE SET NULL` clears it when an
 * assignment goes. A PostgREST `.update()` matching zero rows returns **no
 * error**, and the app does not `.select()` to count what it changed, so its
 * `if (error)` branch never fires. It fails completely silently.
 *
 * ── Why these rows can never heal on their own ──
 *
 * The only thing that would write `completed` is the completion event, and that
 * already happened. It does not happen twice. So the row sits below terminal
 * forever, the recall list keeps offering it as available work, and every route
 * view used to rewrite it to `viewed`.
 *
 * ── Why the web can fix it without an app release ──
 *
 * The same leverage as the release-disposition work: the assignment row is
 * still there and still says `completed`. That is evidence, not a guess, so the
 * web can correct the lifecycle record from it — for every app build already in
 * the field, including ones that will never be updated.
 *
 * Deliberately conservative about what it claims:
 *   - Only the OUTCOME is written. No servicing technician is invented; we know
 *     the work ended, not necessarily who finished it. Fuller reconstruction is
 *     the backend repair's job (B11/B12), which has more evidence to work with.
 *   - `cancelled` gets no `final_status`, because the assignment records that it
 *     ended but not why. Recording "ended" without a reason is honest; picking
 *     a reason would not be.
 *   - It acts only where the assignment and the lifecycle record AGREE on how
 *     the recall ended, and each agreement is checked inside its own UPDATE,
 *     so nothing can change between checking and writing. Where they disagree
 *     it does nothing. The rules, and why, are in planReconciliation.
 *   - Each write is a guarded advance like every other write here, so a row
 *     that is already terminal is untouched and nothing can move backwards.
 */
async function reconcileLostCompletion(
  identity: RecallIdentity,
  plan: ReconciliationPlan
): Promise<boolean> {
  if (!isWebWritableState(plan.target)) return false;

  const prodClient = getProdSupabaseClient();
  const now = new Date().toISOString();
  const advanceFrom = getLowerRankedStates(plan.target);

  // The arms are mutually exclusive — a row has exactly one final_status — so
  // they are issued together, and at most one of them can match.
  const matched = await Promise.all(
    plan.arms.map(async arm => {
      const { data, error } = await prodClient
        .from('recall_lifecycle_records')
        .update({
          ...arm.fields,
          lifecycle_state: plan.target,
          lifecycle_state_updated_at: now,
          updated_at: now
        } as never)
        .eq('vin', identity.vin)
        .eq('campaign_number', identity.campaignNumber)
        .eq('tenant_id', identity.tenantId)
        .in('lifecycle_state', advanceFrom)
        .or(finalStatusFilter(arm.whenFinalStatus))
        .select('id');

      if (error) throw error;
      return (data?.length ?? 0) > 0;
    })
  );

  return matched.some(Boolean);
}

/**
 * Update lifecycle record when a recall is moved to a different date.
 *
 * Re-scheduling had no representation anywhere: no state, no column, no
 * timestamp. A date change was written as `lifecycle_state = 'assigned'`, which
 * both erased the fact that the recall had been moved AND downgraded a
 * `confirmed` recall back to `assigned`.
 *
 * `re_scheduled` and `re_assigned` are deliberate PEERS at rank 50 — both mean
 * "assigned, plan revised" — so neither silently overwrites the other, and a
 * recall that is re-assigned and then moved keeps both facts.
 *
 * Worth surfacing beyond the audit trail: a recall pushed three times is an
 * operational problem, a strong negative signal for routing, and the honest
 * answer when a dealer asks why their car has not been seen yet.
 *
 * The durable record of HOW MANY times is `reschedule_count`, which does not
 * exist yet (backend item B2b). Until it lands, the state carries the fact and
 * the count is unavailable — the same weakness re-assignment already has, where
 * the state survived 12 times out of 2,018 while the columns survived all of them.
 */
export async function updateLifecycleOnReschedule(
  assignmentId: string,
  scheduleChange: {
    newVisitDate: string;
    previousVisitDate?: string;
  }
): Promise<void> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) return;

    const assignment = await fetchAssignmentSnapshot(assignmentId, tenantId);
    if (!assignment) return;

    await writeLifecycleRecord(
      { vin: assignment.vin, campaignNumber: assignment.campaign_number, tenantId },
      're_scheduled',
      {
        expected_visit_date: scheduleChange.newVisitDate,
        assignment_status: assignment.assignment_status ?? 'assigned'
      },
      {
        // A revision must always be recordable, including after work has begun.
        // Terminal states are absent: moving a finished recall to a new date is
        // exactly the edit P3 forbids.
        allowRegressionFrom: ['in_progress']
      }
    );
  } catch (error) {
    logSystemError(error, 'recording a re-scheduled visit');
    // Don't throw - this is non-critical
  }
}

/**
 * Update lifecycle record when route is confirmed
 */
export async function updateLifecycleOnConfirmation(assignmentId: string): Promise<void> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) return;

    const assignment = await fetchAssignmentSnapshot(assignmentId, tenantId);
    if (!assignment) return;

    const now = new Date().toISOString();
    // Records `confirmed_at` only. `confirmed` was never once written as a
    // state in eight months of production — zero rows — and it describes the
    // ROUTE, not the recall: confirming a route says the plan is committed, not
    // that this particular recall reached a new stage. The timestamp carries it.
    await recordLifecycleEvent(
      { vin: assignment.vin, campaignNumber: assignment.campaign_number, tenantId },
      {
        confirmed_at: now,
        assignment_status: 'confirmed'
      }
    );
  } catch (error) {
    logSystemError(error, 'recording a route confirmation');
    // Don't throw - this is non-critical
  }
}

/**
 * Update lifecycle record when route is viewed (preview)
 */
export async function updateLifecycleOnView(
  vin: string,
  campaignNumber: string,
  evidence?: AssignmentEvidence
): Promise<void> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) return;

    const identity = { vin, campaignNumber, tenantId };
    const now = new Date().toISOString();
    const assignmentKey = evidence?.assignmentId ? { assignment_id: evidence.assignmentId } : {};

    // If the assignment says this recall is finished, use the visit to REPAIR
    // the lifecycle record rather than merely note it. This is the exact code
    // path that used to destroy these rows, and a route preview touches every
    // recall on a route, so it reaches the damaged population faster than
    // anything else in the app. See planReconciliation (RLM-63).
    const plan = evidence ? planReconciliation(evidence) : null;
    if (plan) {
      if (await reconcileLostCompletion(identity, plan)) return;

      // The records disagree about how it ended, or there is no lifecycle row.
      // Note the view on an existing row only — never create one for finished
      // work, since a new row would start at `selected` and re-offer it.
      await recordLifecycleEvent(
        identity,
        { viewed_at: now, ...assignmentKey },
        { createIfMissing: false }
      );
      return;
    }

    // Live work. Opening a panel is not a workflow stage; `viewed_at` records
    // it, and there is no state for a view to overwrite. The old version of
    // this path read the state then wrote in a separate round trip, and left
    // 25,279 rows in `viewed` — 21,295 carrying an `assigned_at` they had
    // genuinely earned.
    //
    // The assignment id is backfilled because the mobile app writes against
    // it, and it is NULL on 79.9% of rows.
    await recordLifecycleEvent(identity, { viewed_at: now, ...assignmentKey });
  } catch (error) {
    logSystemError(error, 'recording a route preview');
    // Don't throw - this is non-critical
  }
}

/**
 * Update lifecycle record when route is printed
 */
export async function updateLifecycleOnPrint(
  vin: string,
  campaignNumber: string,
  userId?: string
): Promise<void> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) return;

    // Same as the view path: printing is an event on an assigned recall, not a
    // stage of its own. `printed_at` and `printed_by_user_id` record it.
    //
    // Writing it as a state also destroyed the event it was supposed to capture:
    // 5,762 rows sit in `viewed` while carrying a `printed_at`, because the two
    // states took turns overwriting each other while both timestamps survived.
    const now = new Date().toISOString();
    await recordLifecycleEvent(
      { vin, campaignNumber, tenantId },
      {
        printed_at: now,
        ...(userId ? { printed_by_user_id: userId } : {})
      }
    );
  } catch (error) {
    logSystemError(error, 'recording a route print');
    // Don't throw - this is non-critical
  }
}

/**
 * The field reset applied when a recall is RECYCLED back into the pool.
 *
 * This is the one place in the file where an explicit `null` is correct rather
 * than omission. Everywhere else, omitting a key preserves the column; here we
 * genuinely intend to clear the previous attempt's details so the recall looks
 * untouched to whoever picks it up next.
 *
 * A comment in this file previously claimed these fields "will be cleared/reset"
 * on auto-unassign. They were not — the keys were simply left out of the
 * payload, which preserves them. That is why 4,488 recycled recalls still name
 * the technician who released them, and why `get_route_lifecycle_feed`'s join
 * attributes pool work to a technician who is not doing it.
 *
 * ── The boundary, which is P3 and is not optional ──
 *
 * `completed_at`, `serviced_by_technician_id` and `serviced_by_technician_name`
 * are NEVER nulled. Who did the work and when IS the completion record. This
 * function is only ever reached on the recycle path, and the write that carries
 * it permits regression only from assigned/re_assigned/re_scheduled/in_progress
 * — never from a terminal state — so it cannot run against a completed row.
 * Both halves matter: the guard stops it being reached, and the omission here
 * means that even if it were, the completion evidence would survive.
 *
 * The natural way to write this function is to null everything that looks like
 * attempt state. That is the wrong way, and the unit tests assert against it.
 */
function buildRecycleReset(input: {
  now: string;
  otherReason?: string;
}): Partial<RecallLifecycleRecord> {
  return {
    status_updated_at: input.now,

    // Cleared: these describe the attempt that just ended, not the recall.
    assignment_id: null,
    assignment_status: null,
    final_status: null,
    completion_notes: null,
    expected_visit_date: null,
    started_at: null,
    is_preferred: false,

    // The reason the recall came back is worth keeping — it is the only
    // surviving copy once the assignment row is deleted, and it is the signal
    // for why work gets deferred.
    ...(input.otherReason ? { other_reason: input.otherReason } : { other_reason: null })

    // Deliberately ABSENT, so the columns keep their values:
    //   completed_at, serviced_by_technician_id, serviced_by_technician_name
    // Deliberately absent for now: technician_id. Moving the departing
    // technician into re_assigned_from_technician_id is handled by the caller
    // that knows who they were; nulling it blindly here would lose them.
  } as Partial<RecallLifecycleRecord>;
}

/** The outcome details a status change carries. */
interface StatusDetails {
  finalStatus?: 'completed' | 'vehicle_sold' | 'not_available' | 'incomplete' | 'other';
  otherReason?: string;
  servicedByTechnicianId?: string;
  servicedByTechnicianName?: string;
  isPreferred?: boolean;
  shouldAutoUnassign?: boolean;
}

/**
 * Recycling moves a recall BACK to the pool, which is a regression by design:
 * assigned (40) or in_progress (60) down to selected (30).
 *
 * Naming the permitted origins keeps it a guarded conditional write, so a
 * COMPLETED recall can never be recycled back into circulation — which is the
 * difference between "a technician sent this back" and "finished work reappeared
 * as available". Terminal states are absent and assertNoTerminalRegression
 * refuses to let them be added.
 */
const RECYCLE_WRITE_OPTIONS: LifecycleWriteOptions = {
  allowRegressionFrom: ['assigned', 're_assigned', 're_scheduled', 'in_progress']
};

/**
 * Which terminal state does this outcome land in?
 *
 * `completed` is reserved for work performed by US, and should always imply a
 * servicing technician and recorded labour. A technician who arrives to find the
 * job already done is looking at someone else's work: that closes out as
 * `cancelled` with the reason recorded, because filing it as completed would
 * credit us with labour we never performed. Production agrees — of the 99 rows
 * reading "Recall already completed", zero have a service event and zero have a
 * completed_at.
 */
function resolveOutcomeState(statusDetails: StatusDetails): LifecycleState {
  if (statusDetails.finalStatus === 'completed') return 'completed';
  if (statusDetails.shouldAutoUnassign) return 'selected';
  return 'cancelled';
}

/** Who serviced this recall — taken from the caller, or looked up by id. */
async function resolveServicingTechnicianName(
  statusDetails: StatusDetails,
  tenantId: string
): Promise<string | undefined> {
  if (!statusDetails.servicedByTechnicianId) return undefined;
  if (statusDetails.servicedByTechnicianName) return statusDetails.servicedByTechnicianName;

  const prodClient = getProdSupabaseClient();
  const { data, error } = await prodClient
    .from('technicians')
    .select('full_name')
    .eq('id', statusDetails.servicedByTechnicianId)
    .eq('tenant_id', tenantId)
    .maybeSingle();

  if (error) {
    logSystemError(error, 'looking up the servicing technician');
    return undefined;
  }
  return (data as { full_name?: string } | null)?.full_name;
}

/**
 * The payload for a recall that has ENDED — completed or cancelled.
 *
 * The mirror image of buildRecycleReset: that one clears the attempt, this one
 * records it. Nothing here is ever nulled, because this is the evidence.
 */
function buildCloseoutPayload(input: {
  assignmentId: string;
  assignment: AssignmentSnapshot;
  statusDetails: StatusDetails;
  servicedByName: string | undefined;
  now: string;
}): Partial<RecallLifecycleRecord> {
  const { assignmentId, assignment, statusDetails, servicedByName, now } = input;
  return {
    is_preferred: statusDetails.isPreferred || false,
    status_updated_at: now,
    assignment_id: assignmentId,
    completed_at: assignment.completed_at ?? now,
    ...(statusDetails.finalStatus ? { final_status: statusDetails.finalStatus } : {}),
    ...(assignment.assignment_status ? { assignment_status: assignment.assignment_status } : {}),
    ...(assignment.notes ? { completion_notes: assignment.notes } : {}),
    ...(statusDetails.otherReason ? { other_reason: statusDetails.otherReason } : {}),
    ...servicingTechnicianFields(statusDetails.servicedByTechnicianId, servicedByName)
  };
}

/**
 * Who performed the work. Both columns are set together on purpose:
 * `technician_id` is who the recall currently belongs to and
 * `serviced_by_technician_id` is who actually did it, and for a closeout those
 * are the same person. Keeping them in one helper stops the pair drifting apart.
 */
function servicingTechnicianFields(
  technicianId: string | undefined,
  technicianName: string | undefined
): Partial<RecallLifecycleRecord> {
  if (!technicianId) return {};
  return {
    technician_id: technicianId,
    serviced_by_technician_id: technicianId,
    ...(technicianName ? { serviced_by_technician_name: technicianName } : {})
  };
}

/**
 * Update lifecycle record when recall status is updated (completed)
 */
export async function updateLifecycleOnCompletion(
  assignmentId: string,
  statusDetails: {
    finalStatus?: 'completed' | 'vehicle_sold' | 'not_available' | 'incomplete' | 'other';
    otherReason?: string;
    servicedByTechnicianId?: string;
    servicedByTechnicianName?: string;
    isPreferred?: boolean;
    shouldAutoUnassign?: boolean;
  }
): Promise<void> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) return;

    const assignment = await fetchAssignmentSnapshot(assignmentId, tenantId);
    if (!assignment) return;

    const servicedByName = await resolveServicingTechnicianName(statusDetails, tenantId);
    const lifecycleState = resolveOutcomeState(statusDetails);
    const now = new Date().toISOString();

    const recycleInput = {
      now,
      ...(statusDetails.otherReason ? { otherReason: statusDetails.otherReason } : {})
    };
    const lifecycleData = statusDetails.shouldAutoUnassign
      ? buildRecycleReset(recycleInput)
      : buildCloseoutPayload({ assignmentId, assignment, statusDetails, servicedByName, now });

    await writeLifecycleRecord(
      { vin: assignment.vin, campaignNumber: assignment.campaign_number, tenantId },
      lifecycleState,
      lifecycleData,
      statusDetails.shouldAutoUnassign ? RECYCLE_WRITE_OPTIONS : {}
    );
  } catch (error) {
    logSystemError(error, 'recording a recall status change');
    // Don't throw - this is non-critical
  }
}

/**
 * Record a status a dispatcher set by hand, from the recall list or the map.
 *
 * Both pages used to write this table DIRECTLY: a `SELECT id`, then an `UPDATE`
 * or an `INSERT`, with neither error even destructured. That is check-then-act —
 * the same shape as the defect this file was rewritten to remove — and because
 * it bypassed writeLifecycleRecord entirely it could put a recall into a
 * terminal state with no monotonic guard at all. Two byte-identical copies, one
 * per page.
 *
 * Routing both through here makes a manual status change a guarded advance like
 * every other write: it cannot move a recall out of a terminal state, it cannot
 * lose a concurrent update, and its errors are logged rather than discarded.
 *
 * The state mapping is unchanged from what the pages already did — `completed`
 * for a completion, `cancelled` for everything else. Whether "vehicle not
 * available" should instead RECYCLE an unassigned recall is a product decision,
 * recorded in the remediation register rather than changed quietly here.
 */
export async function updateLifecycleOnManualStatusChange(
  vin: string,
  campaignNumber: string,
  outcome: { finalStatus: string; otherReason?: string | undefined }
): Promise<void> {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) return;

    const now = new Date().toISOString();
    const isCompleted = outcome.finalStatus === 'completed';

    await writeLifecycleRecord(
      { vin, campaignNumber, tenantId },
      isCompleted ? 'completed' : 'cancelled',
      {
        final_status: outcome.finalStatus,
        status_updated_at: now,
        ...(isCompleted ? { completed_at: now } : {}),
        ...(outcome.otherReason ? { other_reason: outcome.otherReason } : {})
      }
    );
  } catch (error) {
    logSystemError(error, 'recording a manual status change');
    // Non-critical: the recall was already removed from the list.
  }
}

/** A recall the route preview knows about, with whatever its assignment says. */
export interface PreviewRecallRef {
  vin: string;
  campaignNumber: string;
  assignmentId?: string | undefined;
  assignmentStatus?: string | null | undefined;
  completedAt?: string | null | undefined;
}

interface LifecycleSnapshot {
  lifecycle_state?: string | null;
  final_status?: string | null;
}

/**
 * PostgREST puts `.in()` lists in the URL, so the list has to stay well short of
 * the ~8 KB a server will accept. 200 VINs is roughly 3.4 KB.
 */
const LIFECYCLE_READ_CHUNK = 200;

function recallKey(vin: string, campaignNumber: string): string {
  return `${vin}|${campaignNumber}`;
}

/** Read the lifecycle rows for a set of recalls in as few requests as possible. */
async function fetchLifecycleSnapshots(
  tenantId: string,
  refs: PreviewRecallRef[]
): Promise<Map<string, LifecycleSnapshot>> {
  const prodClient = getProdSupabaseClient();
  const wanted = new Set(refs.map(ref => recallKey(ref.vin, ref.campaignNumber)));
  const vins = [...new Set(refs.map(ref => ref.vin))];
  const snapshots = new Map<string, LifecycleSnapshot>();

  for (let i = 0; i < vins.length; i += LIFECYCLE_READ_CHUNK) {
    const { data, error } = await prodClient
      .from('recall_lifecycle_records')
      .select('vin, campaign_number, lifecycle_state, final_status')
      .eq('tenant_id', tenantId)
      .in('vin', vins.slice(i, i + LIFECYCLE_READ_CHUNK));

    if (error) throw error;
    const rows = (data ?? []) as Array<
      LifecycleSnapshot & { vin: string; campaign_number: string }
    >;
    for (const row of rows) {
      // One VIN can carry several campaigns; keep only what we asked about.
      const key = recallKey(row.vin, row.campaign_number);
      if (wanted.has(key)) snapshots.set(key, row);
    }
  }
  return snapshots;
}

/** Repair the rows that need it, one guarded write each. Usually none. */
async function repairFromSnapshots(
  tenantId: string,
  repairable: PreviewRecallRef[],
  snapshots: Map<string, LifecycleSnapshot>
): Promise<number> {
  let repaired = 0;
  for (const ref of repairable) {
    const snapshot = snapshots.get(recallKey(ref.vin, ref.campaignNumber));
    // Never create a row for finished work: a new one starts at `selected`.
    if (!snapshot) continue;

    const plan = planReconciliation(ref);
    if (!plan) continue;
    const arm = chooseReconciliationArm(plan, snapshot);
    if (!arm) continue;

    const identity = { vin: ref.vin, campaignNumber: ref.campaignNumber, tenantId };
    if (await reconcileLostCompletion(identity, { ...plan, arms: [arm] })) repaired += 1;
  }
  return repaired;
}

/** Record the view for everything the route actually shows, in bulk. */
async function recordViews(
  tenantId: string,
  viewed: PreviewRecallRef[],
  snapshots: Map<string, LifecycleSnapshot>
): Promise<number> {
  const now = new Date().toISOString();
  const rows: Record<string, unknown>[] = [];

  for (const ref of viewed) {
    const exists = snapshots.has(recallKey(ref.vin, ref.campaignNumber));
    // Never create a row for finished work — a new row would start at
    // `selected` and put the recall back into the pool.
    if (!exists && isFinalAssignmentStatus(ref.assignmentStatus ?? undefined)) continue;

    rows.push(
      buildLifecyclePayload(
        { vin: ref.vin, campaign_number: ref.campaignNumber, tenant_id: tenantId },
        {
          viewed_at: now,
          updated_at: now,
          ...(ref.assignmentId ? { assignment_id: ref.assignmentId } : {}),
          // A row that does not exist yet is created at the lowest real stage,
          // rather than inheriting the column default.
          ...(exists ? {} : { lifecycle_state: 'selected' })
        }
      )
    );
  }
  if (rows.length === 0) return 0;

  const prodClient = getProdSupabaseClient();
  // PostgREST builds one ON CONFLICT DO UPDATE SET clause from the first row's
  // keys and requires every row in the batch to carry the same ones, so send one
  // request per key shape. Padding to a uniform shape would erase columns.
  for (const group of partitionUpsertRows(rows)) {
    const { error } = await prodClient
      .from('recall_lifecycle_records')
      .upsert(group as never, { onConflict: 'vin,campaign_number,tenant_id' })
      .select('id');
    if (error) throw error;
  }
  return rows.length;
}

/**
 * One batched pass for a route preview (RLM-06).
 *
 * This used to be one `updateLifecycleOnView` call per recall, over EVERY
 * assignment in the tenant, re-fired on each technician switch: 6,164 requests
 * for the largest tenant. Now it is a bulk read (one request per 200 VINs) plus
 * a handful of writes, and the repairs are issued only for rows the read shows
 * actually need one — which is nearly always zero after the first pass.
 *
 * The two inputs are deliberately different populations:
 *
 *   `viewed`      what the route actually shows — one technician, one day, still
 *                 live work. Recording `viewed_at` against every recall in the
 *                 tenant, which is what the old code did, made the column
 *                 meaningless.
 *
 *   `repairable`  finished assignments anywhere in the tenant. The repair has to
 *                 keep this wider reach: narrowing it to the visible route would
 *                 silently switch off the backlog correction (RLM-63).
 */
export async function syncLifecycleForRoutePreview(input: {
  viewed: PreviewRecallRef[];
  repairable: PreviewRecallRef[];
}): Promise<{ viewedWritten: number; repaired: number }> {
  const empty = { viewedWritten: 0, repaired: 0 };
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);
    if (!tenantId) return empty;

    const all = [...input.viewed, ...input.repairable];
    if (all.length === 0) return empty;

    const snapshots = await fetchLifecycleSnapshots(tenantId, all);
    const repaired = await repairFromSnapshots(tenantId, input.repairable, snapshots);
    const viewedWritten = await recordViews(tenantId, input.viewed, snapshots);
    return { viewedWritten, repaired };
  } catch (error) {
    logSystemError(error, 'recording a route preview');
    return empty;
  }
}

/**
 * Get lifecycle records for a specific recall (VIN + campaign)
 */
export async function getRecallLifecycleRecords(
  vin: string,
  campaignNumber: string
): Promise<RecallLifecycleRecord[]> {
  const prodClient = getProdSupabaseClient();
  const tenantId = await getTenantIdFromSession(prodClient);

  if (!tenantId) {
    throw new Error('Unable to determine tenant association');
  }

  const { data, error } = await prodClient
    .from('recall_lifecycle_records')
    .select('*')
    .eq('vin', vin)
    .eq('campaign_number', campaignNumber)
    .eq('tenant_id', tenantId)
    .order('lifecycle_state_updated_at', { ascending: false });

  if (error) {
    logSystemError(error, 'fetching recall lifecycle records');
    throw new Error(`Failed to fetch lifecycle records: ${error.message}`);
  }

  return data || [];
}
